import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Lightbulb, MapPin as MapPinned, Rocket, Lightning as Bolt, Lock, Timer, ChatCircle as MessageCircle, WhatsappLogo } from '@phosphor-icons/react';
import LandingNav from './LandingNav';
import LandingFooter from './LandingFooter';
import SEO, { organizationJsonLd } from '../../components/SEO';

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { node.classList.add('revealed'); obs.unobserve(node); } },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);
  return ref;
};

const R = ({ children, className = '', delay = 0 }) => {
  const ref = useReveal();
  return <div ref={ref} className={`reveal-on-scroll ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
};

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-primaryB-50 overflow-x-hidden">
      <SEO
        title="About – POS Software for African Businesses"
        description="SmartPOS Africa is built for African entrepreneurs. We're building the POS software and business management tools that African businesses need to compete and thrive – starting with a POS that actually works for you."
        path="/about"
        keywords="about SmartPOS Africa, African business software company, POS software company Ghana, POS software company Nigeria, software solutions Africa, African tech startup, pos africa"
        jsonLd={organizationJsonLd}
      />
      <LandingNav />

      {/* ━━━ HERO ━━━ */}
      <section className="pt-24 sm:pt-32 lg:pt-40 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <R>
              <p className="text-emerald-600 text-sm font-bold tracking-wide uppercase mb-4">About Smart POS</p>
            </R>
            <R delay={80}>
              <h1 className="text-3xl sm:text-4xl lg:text-[3.25rem] font-extrabold text-primaryB-900 leading-[1.15] tracking-tight mb-6">
                Built for African entrepreneurs.
              </h1>
            </R>
            <R delay={160}>
              <p className="text-primaryB-500 text-base sm:text-lg leading-relaxed max-w-xl">
                We're building the tools that African businesses need to compete and thrive – starting with a POS that actually works for you.
              </p>
            </R>
          </div>
        </div>
      </section>

      {/* ━━━ STATS BAR ━━━ */}
      <section className="border-y border-primaryB-200/60 bg-primaryB-50">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-primaryB-200/60">
            {[
              { value: '50+', label: 'Businesses served' },
              { value: '1 month', label: 'Free trial' },
              { value: '2', label: 'Countries' },
              { value: '5 min', label: 'Setup time' },
            ].map((stat, i) => (
              <div key={stat.label} className="py-6 sm:py-10 text-center px-2">
                <div className="text-xl sm:text-3xl font-extrabold text-primaryB-900 mb-0.5 sm:mb-1">{stat.value}</div>
                <div className="text-xs sm:text-sm text-primaryB-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ MISSION – big pull-quote style ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">
            {/* Left – pull quote */}
            <div className="lg:col-span-2">
              <R>
                <div className="lg:sticky lg:top-32">
                  <div className="w-12 h-1 bg-emerald-500 rounded-full mb-6" />
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-primaryB-900 tracking-tight leading-tight mb-4">
                    Our mission
                  </h2>
                  <p className="text-primaryB-300 text-sm">
                    Why we started, and where we're going.
                  </p>
                </div>
              </R>
            </div>

            {/* Right – narrative text */}
            <div className="lg:col-span-3">
              <R delay={100}>
                <p className="text-primaryB-900 text-xl sm:text-2xl font-bold leading-relaxed mb-6">
                  "Every day, millions of African entrepreneurs run their businesses with pen and paper, or expensive software that doesn't fit their needs."
                </p>
              </R>
              <R delay={180}>
                <p className="text-primaryB-500 text-base sm:text-lg leading-relaxed mb-5">
                  We believe there's a better way. Smart POS gives you the tools to track every sale, manage your inventory, understand your customers, and make better business decisions – all from one simple app.
                </p>
              </R>
              <R delay={240}>
                <p className="text-primaryB-500 text-base sm:text-lg leading-relaxed">
                  We built Smart POS because the tools available to African businesses were either too expensive, too complicated, or designed for markets that look nothing like ours. We're changing that – one business at a time.
                </p>
              </R>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ VALUES – numbered vertical list, not cards ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28 bg-primaryB-900">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <p className="text-emerald-400 text-sm font-bold tracking-wide uppercase mb-4">What we believe</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-12 sm:mb-16">
              Three principles that guide us.
            </h2>
          </R>

          <div className="space-y-0">
            {[
              {
                num: '01',
                icon: Lightbulb,
                title: 'Simplicity first',
                description: 'Technology should make your life easier, not harder. We build tools that are powerful yet simple to use. If it takes more than a minute to learn, we redesign it.',
              },
              {
                num: '02',
                icon: MapPinned,
                title: 'Built for where you are',
                description: 'We understand the unique challenges of running a business in Africa. Offline mode, mobile money, local currencies – it\'s all built in because we know you need it.',
              },
              {
                num: '03',
                icon: Rocket,
                title: 'Always shipping',
                description: 'We listen to feedback and ship new features regularly. Your subscription gets better over time at no extra cost. Every week, Smart POS gets smarter.',
              },
            ].map((value, i) => {
              const Icon = value.icon;
              return (
                <R key={value.title} delay={i * 120}>
                  <div className={`flex gap-5 sm:gap-8 py-8 sm:py-10 ${i < 2 ? 'border-b border-white/[0.08]' : ''}`}>
                    <div className="flex-shrink-0 pt-1">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-primaryB-50/[0.06] flex items-center justify-center">
                        <Icon weight="bold" className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" strokeWidth={1.5} />
                      </div>
                    </div>
                    <div>
                      <span className="text-white/20 text-xs font-bold tracking-widest">{value.num}</span>
                      <h3 className="text-lg sm:text-xl font-bold text-white mt-1 mb-2">{value.title}</h3>
                      <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-lg">{value.description}</p>
                    </div>
                  </div>
                </R>
              );
            })}
          </div>
        </div>
      </section>

      {/* ━━━ WHY US – horizontal scroll on mobile, grid on desktop ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-10 sm:mb-14">
              <div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primaryB-900 tracking-tight">
                  Why businesses choose us
                </h2>
              </div>
              <Link
                to="/features"
                className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold text-sm transition-all duration-200"
              >
                See all features <ArrowRight weight="bold" className="w-4 h-4" />
              </Link>
            </div>
          </R>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
            {[
              {
                icon: Bolt,
                title: 'Fast & reliable',
                description: 'Lightning-fast performance that keeps up with your busiest days. Works even when your internet doesn\'t.',
                accent: 'bg-amber-500/[0.08]',
                iconColor: 'text-amber-600',
              },
              {
                icon: Lock,
                title: 'Secure & private',
                description: 'Your business data is encrypted and protected. We never share your information with third parties.',
                accent: 'bg-blue-500/[0.08]',
                iconColor: 'text-blue-600',
              },
              {
                icon: Timer,
                title: 'Save hours every week',
                description: 'Automate tedious tasks like inventory tracking, reports, and staff management. Focus on what matters.',
                accent: 'bg-violet-500/[0.08]',
                iconColor: 'text-violet-600',
              },
              {
                icon: MessageCircle,
                title: 'Real human support',
                description: 'Our team is always ready to help via WhatsApp or email. We\'re here when you need us – not a bot.',
                accent: 'bg-rose-500/[0.08]',
                iconColor: 'text-rose-600',
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <R key={item.title} delay={i * 80}>
                  <div className="bg-primaryB-50 rounded-3xl p-6 sm:p-7 border border-primaryB-200/60 hover:shadow-elevated transition-all duration-300 h-full group">
                    <div className={`w-11 h-11 rounded-xl ${item.accent} flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110`}>
                      <Icon weight="bold" className={`w-5 h-5 ${item.iconColor}`} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-primaryB-900 mb-1.5">{item.title}</h3>
                    <p className="text-primaryB-500 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </R>
              );
            })}
          </div>
        </div>
      </section>

      {/* ━━━ CONTACT CTA ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28 border-t border-primaryB-200/60">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <R>
            <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <WhatsappLogo weight="fill" className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primaryB-900 tracking-tight mb-4">
              We're here when you need us.
            </h2>
            <p className="text-primaryB-500 text-base sm:text-lg leading-relaxed mb-8 max-w-xl mx-auto">
              Got a question, need help with setup, or want to renew your subscription? Chat with us directly on WhatsApp — a real person will respond.
            </p>
            <a
              href="https://wa.me/233559306229"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-200 text-base"
            >
              <WhatsappLogo weight="fill" className="w-5 h-5" />
              Chat with us on WhatsApp
            </a>
          </R>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default AboutPage;
